import { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, X, Phone, Mail, Building2, FileText, Send, StickyNote,
  Percent, Save, Download, Sparkles, GripVertical, Plus, Paperclip, Pencil, Check, MapPin, CalendarClock, Trash2, History, Copy, Search, BellRing,
} from "lucide-react";
import { EMAIL_TEMPLATES } from "@/lib/emailTemplates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { id: "new", label: "New", dot: "#64748b" },
  { id: "contacted", label: "Contacted", dot: "#0ea5e9" },
  { id: "proposal_sent", label: "Proposal Sent", dot: "#f59e0b" },
  { id: "won", label: "Won", dot: "#059669" },
  { id: "lost", label: "Lost", dot: "#ef4444" },
];

const money = (v) => (v || v === 0 ? `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : null);
const NUDGE_AFTER_MS = 7 * 24 * 60 * 60 * 1000;
const isNudgeLead = (lead, now = Date.now()) => {
  if (!lead?.created_at || ["won", "lost"].includes(lead.stage)) return false;
  const created = new Date(lead.created_at).getTime();
  return Number.isFinite(created) && now - created >= NUDGE_AFTER_MS;
};

const easternToday = () => new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());


function AuthImage({ path, token }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let revoked;
    (async () => {
      try {
        const res = await axios.get(`${API}/files/${path}`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
        revoked = URL.createObjectURL(res.data);
        setUrl(revoked);
      } catch { /* ignore */ }
    })();
    return () => revoked && URL.revokeObjectURL(revoked);
  }, [path, token]);
  if (!url) return <div className="h-40 grid place-items-center bg-[#F0EFEA] rounded-xl text-slate-400 text-sm"><Loader2 size={18} className="animate-spin" /></div>;
  return <img loading="lazy" decoding="async" src={url} alt="Customer invoice" className="w-full rounded-xl border border-[#E7E5DF]" data-testid="drawer-invoice-img" />;
}

export function LeadDrawer({ lead, token, onClose, onUpdate }) {
  const [note, setNote] = useState("");
  const [editIdx, setEditIdx] = useState(-1);
  const [editTxt, setEditTxt] = useState("");
  const [busy, setBusy] = useState("");

  const addNote = async () => {
    if (!note.trim()) return;
    setBusy("note");
    try {
      const { data } = await axios.post(`${API}/admin/quotes/${lead.id}/notes`, { text: note }, { headers: { Authorization: `Bearer ${token}` } });
      onUpdate({ ...lead, notes: [...(lead.notes || []), data.note] });
      setNote("");
    } catch { toast.error("Failed to add note."); } finally { setBusy(""); }
  };

  const saveNotes = async (newNotes, okMsg) => {
    setBusy("note");
    try {
      const { data } = await axios.put(`${API}/admin/quotes/${lead.id}/notes`, { notes: newNotes }, { headers: { Authorization: `Bearer ${token}` } });
      onUpdate({ ...lead, notes: data.notes });
      if (okMsg) toast.success(okMsg);
    } catch { toast.error("Failed to save memo."); } finally { setBusy(""); setEditIdx(-1); }
  };

  const saveEditedNote = () => {
    if (!editTxt.trim()) { toast.error("Memo can't be empty."); return; }
    const newNotes = (lead.notes || []).map((n, idx) => (idx === editIdx ? { ...n, text: editTxt } : n));
    saveNotes(newNotes, "Memo updated.");
  };

  const deleteNote = (idx) => {
    const newNotes = (lead.notes || []).filter((_, i) => i !== idx);
    saveNotes(newNotes, "Memo deleted.");
  };

  const [compose, setCompose] = useState(false);
  const [pmsg, setPmsg] = useState("");
  const [items, setItems] = useState(() =>
    lead.line_items && lead.line_items.length
      ? lead.line_items.map((it) => ({ qty: it.qty ?? 1, size: it.size || "", purpose: it.purpose || "", frequency: it.frequency || "", price: it.price ?? "" }))
      : [{ qty: lead.quantity || 1, size: lead.container_size || "", purpose: "", frequency: lead.frequency || "", price: lead.projected_amount ?? "" }]
  );
  const setItem = (i, k, v) => setItems((p) => p.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const addItem = () => setItems((p) => [...p, { qty: 1, size: "", purpose: "", frequency: "", price: "" }]);
  const removeItem = (i) => setItems((p) => (p.length > 1 ? p.filter((_, idx) => idx !== i) : p));
  const itemsTotal = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.price) || 0), 0);
  const [fmsg, setFmsg] = useState("");
  const [fwhen, setFwhen] = useState("");
  const [reminder, setReminder] = useState(null);
  const [reminderDate, setReminderDate] = useState(easternToday());

  useEffect(() => {
    let active = true;
    axios.get(`${API}/admin/leads/${lead.id}/followup-reminder`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => { if (active) { setReminder(data.task); setReminderDate(data.task?.due_date || ""); } })
      .catch(() => {});
    return () => { active = false; };
  }, [lead.id, token]);

  const notifyFollowupsChanged = () => window.dispatchEvent(new CustomEvent("ews:followups-changed"));

  const saveReminder = async () => {
    if (!reminderDate) { toast.error("Pick a follow-up date."); return; }
    setBusy("reminder");
    try {
      const { data } = await axios.post(`${API}/admin/leads/${lead.id}/followup-reminder`, { due_date: reminderDate }, { headers: { Authorization: `Bearer ${token}` } });
      setReminder(data);
      notifyFollowupsChanged();
      toast.success("Follow-up reminder saved — internal only.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save reminder."); }
    finally { setBusy(""); }
  };

  const clearReminder = async () => {
    setBusy("clearReminder");
    try {
      await axios.delete(`${API}/admin/leads/${lead.id}/followup-reminder`, { headers: { Authorization: `Bearer ${token}` } });
      setReminder(null);
      setReminderDate("");
      notifyFollowupsChanged();
      toast.success("Follow-up reminder cleared.");
    } catch { toast.error("Could not clear reminder."); }
    finally { setBusy(""); }
  };

  const sendFollowup = async (schedule) => {
    if (schedule && !fwhen) { toast.error("Pick a date & time to schedule."); return; }
    setBusy(schedule ? "fschedule" : "fsend");
    try {
      const body = { message: fmsg || "" };
      if (schedule) body.scheduled_at = new Date(fwhen).toISOString();
      const { data } = await axios.post(`${API}/admin/quotes/${lead.id}/followup`, body, { headers: { Authorization: `Bearer ${token}` } });
      if (schedule) {
        onUpdate({ ...lead, follow_up_scheduled_at: body.scheduled_at, follow_up_sent_at: null });
        toast.success("Follow-up scheduled.");
        setFwhen("");
      } else {
        onUpdate({ ...lead, follow_up_sent_at: data.follow_up_sent_at || new Date().toISOString(), follow_up_scheduled_at: null });
        toast[data.email_sent ? "success" : "error"](data.email_sent ? "Follow-up email sent." : "Could not send (no email?).");
      }
      setFmsg("");
    } catch { toast.error("Follow-up failed."); } finally { setBusy(""); }
  };

  const sendProposal = async () => {
    const cleanItems = items
      .filter((it) => Number(it.price) > 0)
      .map((it) => ({ qty: Number(it.qty) || 1, size: (it.size || "").trim(), purpose: (it.purpose || "").trim(), frequency: (it.frequency || "").trim(), price: Number(it.price) || 0 }));
    setBusy("proposal");
    try {
      const body = { message: pmsg };
      if (cleanItems.length) body.line_items = cleanItems;
      const { data } = await axios.post(`${API}/admin/quotes/${lead.id}/proposal`, body, { headers: { Authorization: `Bearer ${token}` } });
      const patch = { stage: "proposal_sent", proposal_sent_at: data.proposal_sent_at || new Date().toISOString(), proposal_path: data.proposal_path };
      if (data.proposal) patch.proposals = [...(lead.proposals || []), data.proposal];
      if (cleanItems.length) {
        patch.line_items = cleanItems;
        patch.projected_amount = itemsTotal;
        patch.quantity = cleanItems.reduce((s, it) => s + it.qty, 0);
        patch.container_size = cleanItems.filter((it) => it.size).map((it) => `${it.qty}× ${it.size}`).join(", ") || lead.container_size;
        if (lead.current_amount) {
          patch.savings_monthly = lead.current_amount - itemsTotal;
          patch.savings_annual = (lead.current_amount - itemsTotal) * 12;
        }
      }
      onUpdate({ ...lead, ...patch });
      setCompose(false);
      toast.success(data.email_sent ? "Proposal generated & emailed to the customer." : "Proposal PDF generated (email not sent — check address).");
    } catch { toast.error("Failed to send proposal."); } finally { setBusy(""); }
  };

  const lastProposal = (lead.proposals || [])[(lead.proposals || []).length - 1];
  const copyFromLast = () => {
    if (!lastProposal) return;
    if (lastProposal.line_items && lastProposal.line_items.length) {
      setItems(lastProposal.line_items.map((it) => ({ qty: it.qty ?? 1, size: it.size || "", purpose: it.purpose || "", frequency: it.frequency || "", price: it.price ?? "" })));
    }
    if (lastProposal.message) setPmsg(lastProposal.message);
    toast.success("Copied from last proposal — tweak the price and resend");
  };

  // ---- Inline customer editing ----
  const [editing, setEditing] = useState(false);
  const [ef, setEf] = useState({
    name: lead.name || "", business: lead.business || "", phone: lead.phone || "", email: lead.email || "",
    service: lead.service || "", service_address: lead.service_address || "",
    container_size: lead.container_size || "", frequency: lead.frequency || "", quantity: lead.quantity ?? "",
    hauler: lead.hauler || "", contract_expiration: lead.contract_expiration || "",
    current_amount: lead.current_amount ?? "", projected_amount: lead.projected_amount ?? "", message: lead.message || "",
  });
  const setE = (k) => (e) => setEf((p) => ({ ...p, [k]: e.target.value }));

  const saveEdits = async () => {
    if (!ef.name.trim()) { toast.error("Contact name is required."); return; }
    setBusy("edit");
    try {
      const body = {
        name: ef.name, business: ef.business, phone: ef.phone, email: ef.email,
        service: ef.service, service_address: ef.service_address,
        container_size: ef.container_size, frequency: ef.frequency,
        quantity: ef.quantity !== "" ? Number(ef.quantity) : null,
        hauler: ef.hauler, contract_expiration: ef.contract_expiration,
        current_amount: ef.current_amount !== "" ? Number(ef.current_amount) : null,
        projected_amount: ef.projected_amount !== "" ? Number(ef.projected_amount) : null,
        message: ef.message,
      };
      const { data } = await axios.patch(`${API}/admin/quotes/${lead.id}`, body, { headers: { Authorization: `Bearer ${token}` } });
      onUpdate(data);
      setEditing(false);
      toast.success("Customer details updated.");
    } catch { toast.error("Failed to save changes."); } finally { setBusy(""); }
  };

  const efField = (label, k, type = "text", ph = "") => (
    <div>
      <label className="block text-[11px] font-mono-tech uppercase tracking-wider text-slate-400 mb-1">{label}</label>
      <input type={type} value={ef[k]} onChange={setE(k)} placeholder={ph} data-testid={`edit-${k}`}
        className="w-full px-3 py-2.5 rounded-xl bg-white border border-[#E7E5DF] text-sm text-[#0B0F14] focus:outline-none focus:border-emerald-600/60" />
    </div>
  );

  return createPortal((
    <div className="fixed inset-0 z-[100] flex justify-center sm:items-center" data-testid="lead-drawer">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full h-full sm:h-auto sm:max-h-[92vh] sm:max-w-5xl sm:mx-4 sm:rounded-2xl bg-[#FBFAF8] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-[#0E3B3B] text-white px-4 sm:px-6 py-4 sm:py-5 flex items-start justify-between z-10 sm:rounded-t-2xl">
          <div>
            <h3 className="font-display font-extrabold text-xl">{lead.name}</h3>
            {lead.business && <p className="text-white/70 text-sm flex items-center gap-1.5 mt-0.5"><Building2 size={13} /> {lead.business}</p>}
            <span className="inline-block mt-2 px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 font-mono-tech text-[10px] uppercase tracking-wider">{lead.service}</span>
          </div>
          <div className="flex items-center gap-2">
            {!editing && (
              <button onClick={() => setEditing(true)} data-testid="drawer-edit"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition-colors">
                <Pencil size={13} /> Edit
              </button>
            )}
            <button onClick={onClose} data-testid="drawer-close" className="text-white/70 hover:text-white"><X size={22} /></button>
          </div>
        </div>

        <div className="p-4 sm:p-6 space-y-6">
          {editing ? (
            <div className="rounded-2xl border border-emerald-200 bg-white p-4 space-y-4" data-testid="lead-edit-form">
              <p className="flex items-center gap-1.5 font-semibold text-[#0B0F14] text-sm"><Pencil size={15} className="text-emerald-600" /> Edit Customer</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {efField("Contact Name *", "name", "text", "Jane Doe")}
                {efField("Business Name", "business", "text", "Doe's Diner")}
                {efField("Telephone", "phone", "tel", "(305) 555-0100")}
                {efField("Email", "email", "email", "jane@business.com")}
                <div className="sm:col-span-2">{efField("Service Address", "service_address", "text", "123 Main St, Miami, FL")}</div>
                {efField("Service", "service", "text", "Business Waste / Roll-Off")}
                {efField("Container Size", "container_size", "text", "e.g. 4 yard")}
                {efField("Frequency", "frequency", "text", "e.g. 2x / week")}
                {efField("Quantity", "quantity", "number", "1")}
                {efField("Current Hauler", "hauler", "text", "e.g. Waste Management")}
                {efField("Contract Expiration", "contract_expiration", "date")}
                {efField("Current Competitor Price ($/mo)", "current_amount", "number", "e.g. 310")}
                {efField("My Offered Price ($/mo)", "projected_amount", "number", "e.g. 240")}
              </div>
              <div>
                <label className="block text-[11px] font-mono-tech uppercase tracking-wider text-slate-400 mb-1">Notes / Message</label>
                <textarea value={ef.message} onChange={setE("message")} rows={2} data-testid="edit-message"
                  className="w-full px-3 py-2.5 rounded-xl bg-white border border-[#E7E5DF] text-sm text-[#0B0F14] focus:outline-none focus:border-emerald-600/60 resize-none" />
              </div>
              <div className="flex gap-2">
                <button onClick={saveEdits} disabled={busy === "edit"} data-testid="drawer-save-edit"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60">
                  {busy === "edit" ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save Changes
                </button>
                <button onClick={() => setEditing(false)} data-testid="drawer-cancel-edit"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#E7E5DF] text-slate-600 text-sm font-semibold hover:border-slate-400">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-2 text-sm">
              {lead.phone && <a href={`tel:${lead.phone}`} className="flex items-center gap-2 text-[#0B0F14] hover:text-emerald-700"><Phone size={15} className="text-emerald-600" /> {lead.phone}</a>}
              {lead.email && <a href={`mailto:${lead.email}`} className="flex items-center gap-2 text-[#0B0F14] hover:text-emerald-700"><Mail size={15} className="text-emerald-600" /> {lead.email}</a>}
              {lead.service_address && <p className="flex items-center gap-2 text-[#0B0F14]"><MapPin size={15} className="text-emerald-600" /> {lead.service_address}</p>}
            </div>
          )}

          {(lead.current_amount || lead.projected_amount || lead.savings_annual) && (
            <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4" data-testid="drawer-savings">
              <p className="flex items-center gap-1.5 text-emerald-700 font-mono-tech text-[10px] uppercase tracking-wider mb-2"><Sparkles size={12} /> Pricing & Savings</p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div><p className="text-[11px] text-slate-500">Competitor</p><p className="font-display font-bold text-[#0B0F14]">{money(lead.current_amount) || "—"}<span className="text-[10px] font-normal">/mo</span></p></div>
                <div><p className="text-[11px] text-slate-500">My price</p><p className="font-display font-bold text-[#0B0F14]">{money(lead.projected_amount) || "—"}<span className="text-[10px] font-normal">/mo</span></p></div>
                <div><p className="text-[11px] text-slate-500">Saves/yr</p><p className="font-display font-bold text-emerald-600">{money(lead.savings_annual) || "—"}</p></div>
              </div>
            </div>
          )}

          {lead.invoice_path && (
            <div>
              <p className="font-mono-tech text-[10px] uppercase tracking-wider text-slate-400 mb-2">Uploaded Invoice</p>
              <AuthImage path={lead.invoice_path} token={token} />
            </div>
          )}

          {!editing && lead.line_items && lead.line_items.length > 0 && (
            <div className="rounded-2xl border border-[#E7E5DF] bg-white p-4" data-testid="drawer-line-items">
              <p className="font-mono-tech text-[10px] uppercase tracking-wider text-slate-400 mb-2">Proposed Containers</p>
              <div className="space-y-1.5">
                {lead.line_items.map((it, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-[#0B0F14]">{it.qty}× {it.size}{it.purpose ? ` · ${it.purpose}` : ""}{it.frequency ? ` · ${it.frequency}` : ""}</span>
                    <span className="text-slate-600 shrink-0">{money((it.qty || 1) * (it.price || 0))}/mo</span>
                  </div>
                ))}
              </div>
              <div className="mt-2 pt-2 border-t border-[#E7E5DF] flex items-center justify-between text-sm font-semibold">
                <span className="text-[#0B0F14]">Total</span>
                <span className="text-emerald-700">{money(lead.line_items.reduce((s, it) => s + (it.qty || 1) * (it.price || 0), 0))}/mo</span>
              </div>
            </div>
          )}

          {!editing && (lead.service_address || lead.container_size || lead.frequency || lead.quantity || lead.hauler || lead.contract_expiration || lead.message) && (
            <div className="text-sm grid grid-cols-2 gap-x-4 gap-y-1.5 text-slate-600">
              {lead.container_size && <p><span className="text-slate-400">Size:</span> {lead.container_size}</p>}
              {lead.frequency && <p><span className="text-slate-400">Frequency:</span> {lead.frequency}</p>}
              {lead.quantity ? <p><span className="text-slate-400">Quantity:</span> {lead.quantity}</p> : null}
              {lead.current_amount ? <p><span className="text-slate-400">Competitor price:</span> {money(lead.current_amount)}/mo</p> : null}
              {lead.projected_amount ? <p><span className="text-slate-400">My offered price:</span> {money(lead.projected_amount)}/mo</p> : null}
              {lead.hauler && <p><span className="text-slate-400">Current hauler:</span> {lead.hauler}</p>}
              {lead.contract_expiration && <p><span className="text-slate-400">Contract ends:</span> {lead.contract_expiration}</p>}
              {lead.message && <p className="col-span-2 italic">&quot;{lead.message}&quot;</p>}
            </div>
          )}

          {/* Proposal */}
          <div className="rounded-2xl border border-[#E7E5DF] bg-white p-4">
            <p className="flex items-center gap-1.5 font-semibold text-[#0B0F14] text-sm mb-2"><FileText size={15} className="text-emerald-600" /> Proposal</p>
            {lead.proposal_sent_at ? (
              <p className="text-xs text-slate-500 mb-3">Sent {new Date(lead.proposal_sent_at).toLocaleString()}</p>
            ) : (
              <p className="text-xs text-slate-500 mb-3">Generate a branded PDF proposal and email it to the customer.</p>
            )}
            {compose && (
              <div className="space-y-3 mb-3" data-testid="proposal-compose">
                {lastProposal && (
                  <button type="button" onClick={copyFromLast} data-testid="proposal-copy-last"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-emerald-300 text-emerald-700 text-xs font-semibold hover:bg-emerald-50 transition-colors">
                    <Copy size={13} /> Copy from previous (v{lead.proposals.length})
                  </button>
                )}
                <textarea value={pmsg} onChange={(e) => setPmsg(e.target.value)} rows={3} data-testid="proposal-message"
                  placeholder="Personal message to include in the proposal (optional)…"
                  className="w-full px-3 py-2.5 rounded-xl bg-white border border-[#E7E5DF] text-sm text-[#0B0F14] focus:outline-none focus:border-emerald-600/60 resize-none" />
                <div className="flex items-center justify-between">
                  <label className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-400">Containers &amp; Pricing</label>
                  <span className="text-xs font-semibold text-emerald-700" data-testid="proposal-items-total">Total: ${itemsTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo</span>
                </div>
                {items.map((it, i) => {
                  const lbl = "block text-[9px] font-mono-tech uppercase tracking-wider text-slate-400 mb-0.5";
                  const inp = "w-full px-2 py-1.5 rounded-lg bg-white border border-[#E7E5DF] text-sm text-[#0B0F14] focus:outline-none focus:border-emerald-600/60";
                  const lineTotal = (Number(it.qty) || 0) * (Number(it.price) || 0);
                  return (
                    <div key={i} className="rounded-xl border border-[#E7E5DF] bg-[#FBFAF8] p-2.5" data-testid={`proposal-item-${i}`}>
                      <div className="grid grid-cols-2 sm:grid-cols-12 gap-2 items-end">
                        <div className="sm:col-span-1">
                          <label className={lbl}>Qty</label>
                          <input type="number" min="1" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} className={inp} data-testid={`item-qty-${i}`} />
                        </div>
                        <div className="sm:col-span-3">
                          <label className={lbl}>Size</label>
                          <input value={it.size} onChange={(e) => setItem(i, "size", e.target.value)} placeholder="e.g. 2 yard" className={inp} data-testid={`item-size-${i}`} />
                        </div>
                        <div className="sm:col-span-3">
                          <label className={lbl}>Purpose</label>
                          <input value={it.purpose} onChange={(e) => setItem(i, "purpose", e.target.value)} placeholder="Trash / Recycling" className={inp} data-testid={`item-purpose-${i}`} />
                        </div>
                        <div className="sm:col-span-2">
                          <label className={lbl}>Schedule</label>
                          <input value={it.frequency} onChange={(e) => setItem(i, "frequency", e.target.value)} placeholder="2x / week" className={inp} data-testid={`item-freq-${i}`} />
                        </div>
                        <div className="sm:col-span-2">
                          <label className={lbl}>$/mo each</label>
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                            <input type="number" min="0" value={it.price} onChange={(e) => setItem(i, "price", e.target.value)} placeholder="0" className={`${inp} pl-5`} data-testid={`item-price-${i}`} />
                          </div>
                        </div>
                        <div className="sm:col-span-1 flex sm:justify-center">
                          <button type="button" onClick={() => removeItem(i)} disabled={items.length === 1} data-testid={`item-remove-${i}`}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30 transition-colors" title="Remove container">
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </div>
                      <p className="mt-1.5 text-[11px] text-slate-500">
                        Line total: <b className="text-[#0B0F14]">${lineTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo</b>
                        {(Number(it.qty) || 0) > 1 && Number(it.price) > 0 ? ` (${it.qty} × $${Number(it.price).toLocaleString()})` : ""}
                      </p>
                    </div>
                  );
                })}
                <button type="button" onClick={addItem} data-testid="proposal-add-item"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-emerald-400 text-emerald-700 text-xs font-semibold hover:bg-emerald-50 transition-colors">
                  <Plus size={14} /> Add container
                </button>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {!compose ? (
                <button onClick={() => setCompose(true)} data-testid="drawer-compose-proposal"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#0E3B3B] text-white text-sm font-semibold hover:bg-[#0A2C2C] transition-colors">
                  <FileText size={15} /> {lead.proposal_sent_at ? "New Personalized Proposal" : "Personalize Proposal"}
                </button>
              ) : (
                <>
                  <button onClick={sendProposal} disabled={busy === "proposal"} data-testid="drawer-send-proposal"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-60">
                    {busy === "proposal" ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send Proposal
                  </button>
                  <button onClick={() => setCompose(false)} data-testid="drawer-cancel-proposal"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#E7E5DF] text-slate-600 text-sm font-semibold hover:border-slate-400">
                    Cancel
                  </button>
                </>
              )}
              {lead.proposal_path && !compose && (
                <a href={`${API}/proposal/${lead.id}/download`} target="_blank" rel="noreferrer" data-testid="drawer-download-proposal"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#E7E5DF] text-[#0B0F14] text-sm font-semibold hover:border-emerald-600/50">
                  <Download size={15} /> View PDF
                </a>
              )}
            </div>

            {/* Proposal history */}
            {lead.proposals && lead.proposals.length > 0 && (
              <div className="mt-4 pt-4 border-t border-[#E7E5DF]" data-testid="proposal-history">
                <p className="flex items-center gap-1.5 font-mono-tech text-[10px] uppercase tracking-wider text-slate-400 mb-2.5">
                  <History size={13} /> Proposal History ({lead.proposals.length})
                </p>
                <ol className="space-y-2">
                  {lead.proposals.slice().reverse().map((p, idx) => {
                    const version = lead.proposals.length - idx;
                    const isLatest = idx === 0;
                    const itemsSummary = (p.line_items && p.line_items.length)
                      ? p.line_items.map((it) => `${it.qty}× ${it.size}${it.purpose ? ` ${it.purpose}` : ""}`).join(", ")
                      : (p.container_size || "");
                    return (
                      <li key={p.id} className={`rounded-xl border p-3 ${isLatest ? "border-emerald-300 bg-emerald-50/40" : "border-[#E7E5DF] bg-white"}`} data-testid={`proposal-version-${p.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-bold text-[#0B0F14]">v{version}</span>
                              {isLatest && <span className="px-1.5 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-semibold">Latest</span>}
                              {p.projected_amount != null && <span className="text-sm font-semibold text-emerald-700">{money(p.projected_amount)}/mo</span>}
                              {p.email_sent === false && <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold">Email failed</span>}
                            </div>
                            <p className="text-[11px] text-slate-500 mt-0.5">{p.sent_at ? new Date(p.sent_at).toLocaleString() : ""}{p.author ? ` · ${p.author}` : ""}</p>
                            {itemsSummary && <p className="text-xs text-slate-600 mt-1 truncate">{itemsSummary}</p>}
                            {p.message && <p className="text-xs text-slate-500 mt-1 italic line-clamp-2">“{p.message}”</p>}
                          </div>
                          <a href={`${API}/proposal/${lead.id}/version/${p.id}/download`} target="_blank" rel="noreferrer" data-testid={`proposal-version-pdf-${p.id}`}
                            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#E7E5DF] text-[#0B0F14] text-xs font-semibold hover:border-emerald-600/50 transition-colors">
                            <Download size={13} /> PDF
                          </a>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}
          </div>

          {/* Internal follow-up reminder */}
          <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4" data-testid="lead-followup-reminder">
            <p className="flex items-center gap-1.5 font-semibold text-[#0B0F14] text-sm"><CalendarClock size={15} className="text-amber-600" /> Internal Follow-up Reminder</p>
            {reminder && (
              <p className="mt-1 text-xs text-amber-800" data-testid="lead-followup-reminder-status">
                Reminder set for <b>{new Date(`${reminder.due_date}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</b> · owner email at 8:00 AM Eastern
              </p>
            )}
            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <input type="date" value={reminderDate} onChange={(e) => setReminderDate(e.target.value)} data-testid="lead-followup-reminder-date"
                className="flex-1 px-3 py-2.5 rounded-xl bg-white border border-amber-200 text-sm text-[#0B0F14] focus:outline-none focus:border-amber-500" />
              <button onClick={saveReminder} disabled={busy === "reminder"} data-testid="lead-followup-reminder-save"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 disabled:opacity-60">
                {busy === "reminder" ? <Loader2 size={15} className="animate-spin" /> : <CalendarClock size={15} />} {reminder ? "Update" : "Set Reminder"}
              </button>
              {reminder && (
                <button onClick={clearReminder} disabled={busy === "clearReminder"} data-testid="lead-followup-reminder-clear"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-amber-300 text-amber-700 text-sm font-semibold hover:bg-amber-100 disabled:opacity-60">
                  Clear
                </button>
              )}
            </div>
            <p className="mt-2 text-[11px] text-amber-700">Internal only — no customer email is sent.</p>
          </div>

          {/* Follow-up */}
          <div className="rounded-2xl border border-[#E7E5DF] bg-white p-4">
            <p className="flex items-center gap-1.5 font-semibold text-[#0B0F14] text-sm"><Send size={15} className="text-emerald-600" /> Follow-up Email</p>
            {(lead.follow_up_sent_at || lead.follow_up_scheduled_at) && (
              <p className="mt-1 text-xs text-slate-500" data-testid="followup-status">
                {lead.follow_up_scheduled_at && !lead.follow_up_sent_at
                  ? <>Scheduled for <b className="text-emerald-700">{new Date(lead.follow_up_scheduled_at).toLocaleString()}</b></>
                  : <>Last follow-up sent {new Date(lead.follow_up_sent_at).toLocaleString()}</>}
              </p>
            )}
            <select data-testid="followup-template" defaultValue=""
              onChange={(e) => { const t = EMAIL_TEMPLATES.find((x) => x.id === e.target.value); if (t) setFmsg(t.message); }}
              className="mt-3 w-full px-3 py-2.5 rounded-xl bg-[#FBFAF8] border border-[#E7E5DF] text-sm text-[#0B0F14] focus:outline-none focus:border-emerald-600/60">
              <option value="">— Start from a saved template (optional) —</option>
              {EMAIL_TEMPLATES.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <textarea value={fmsg} onChange={(e) => setFmsg(e.target.value)} rows={5} data-testid="followup-message"
              placeholder="Optional custom message (leave blank to use the standard savings reminder)…"
              className="mt-2 w-full px-3 py-2.5 rounded-xl bg-[#FBFAF8] border border-[#E7E5DF] text-sm text-[#0B0F14] focus:outline-none focus:border-emerald-600/60 resize-none" />
            <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
              <button onClick={() => sendFollowup(false)} disabled={busy === "fsend"} data-testid="followup-send-now"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[#0E3B3B] text-white text-sm font-semibold hover:bg-[#0A2C2C] transition-colors disabled:opacity-60">
                {busy === "fsend" ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send Now
              </button>
              <span className="hidden sm:block text-slate-300">·</span>
              <input type="datetime-local" value={fwhen} onChange={(e) => setFwhen(e.target.value)} data-testid="followup-datetime"
                className="px-3 py-2 rounded-xl bg-[#FBFAF8] border border-[#E7E5DF] text-sm text-[#0B0F14] focus:outline-none focus:border-emerald-600/60" />
              <button onClick={() => sendFollowup(true)} disabled={busy === "fschedule"} data-testid="followup-schedule"
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-emerald-600/50 text-emerald-700 text-sm font-semibold hover:bg-emerald-50 transition-colors disabled:opacity-60">
                {busy === "fschedule" ? <Loader2 size={15} className="animate-spin" /> : <CalendarClock size={15} />} Schedule
              </button>
            </div>
          </div>

          {/* Notes */}
          <div>
            <p className="flex items-center gap-1.5 font-semibold text-[#0B0F14] text-sm mb-3"><StickyNote size={15} className="text-emerald-600" /> Notes & Activity</p>
            <div className="space-y-2 mb-3 max-h-52 overflow-y-auto">
              {(lead.notes || []).length === 0 && <p className="text-slate-400 text-sm">No notes yet.</p>}
              {(lead.notes || []).map((n, i) => (
                <div key={i} className="bg-white border border-[#E7E5DF] rounded-xl p-3 text-sm group" data-testid="drawer-note">
                  {editIdx === i ? (
                    <div>
                      <textarea value={editTxt} onChange={(e) => setEditTxt(e.target.value)} rows={3} data-testid="note-edit-input"
                        className="w-full px-2.5 py-2 rounded-lg bg-[#FBFAF8] border border-[#E7E5DF] text-sm text-[#0B0F14] focus:outline-none focus:border-emerald-600/60 resize-none" />
                      <div className="flex gap-2 mt-2">
                        <button onClick={saveEditedNote} disabled={busy === "note"} data-testid="note-save"
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-60">
                          {busy === "note" ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Save
                        </button>
                        <button onClick={() => setEditIdx(-1)} data-testid="note-cancel"
                          className="px-3 py-1.5 rounded-lg border border-[#E7E5DF] text-slate-600 text-xs font-semibold hover:border-slate-400">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-[#0B0F14] whitespace-pre-wrap flex-1">{n.text}</p>
                        <div className="flex gap-1.5 shrink-0 opacity-60 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => { setEditIdx(i); setEditTxt(n.text); }} data-testid={`note-edit-${i}`}
                            className="text-slate-400 hover:text-emerald-700" title="Edit memo"><Pencil size={13} /></button>
                          <button onClick={() => deleteNote(i)} data-testid={`note-delete-${i}`}
                            className="text-slate-400 hover:text-red-600" title="Delete memo"><Trash2 size={13} /></button>
                        </div>
                      </div>
                      <p className="text-slate-400 text-xs mt-1">{n.author} · {new Date(n.created_at).toLocaleString()}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} data-testid="drawer-note-input"
                placeholder="Add a note…" className="flex-1 px-3 py-2.5 rounded-xl bg-white border border-[#E7E5DF] text-sm text-[#0B0F14] focus:outline-none focus:border-emerald-600/60" />
              <button onClick={addNote} disabled={busy === "note"} data-testid="drawer-add-note"
                className="px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60">
                {busy === "note" ? <Loader2 size={15} className="animate-spin" /> : "Add"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  ), document.body);
}

export function NewLeadModal({ token, onClose, onCreated }) {
  const [f, setF] = useState({
    name: "", business: "", phone: "", email: "", service: "Manual Entry",
    container_size: "", quantity: "", frequency: "", hauler: "",
    contract_expiration: "", current_amount: "", price: "", message: "", service_address: "",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    if (!f.name.trim() || !f.phone.trim()) {
      toast.error("Contact name and phone are required.");
      return;
    }
    setBusy(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      let invoice_path = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await axios.post(`${API}/admin/upload`, fd, { headers });
        invoice_path = up.data.path;
      }
      const body = {
        name: f.name, business: f.business, phone: f.phone, email: f.email,
        service: f.service || "Manual Entry",
        container_size: f.container_size, frequency: f.frequency,
        quantity: f.quantity ? Number(f.quantity) : null,
        hauler: f.hauler, contract_expiration: f.contract_expiration,
        current_amount: f.current_amount ? Number(f.current_amount) : null,
        price: f.price ? Number(f.price) : null,
        service_address: f.service_address,
        message: f.message, invoice_path,
      };
      const { data } = await axios.post(`${API}/admin/quotes`, body, { headers });
      toast.success("Lead added.");
      onCreated(data);
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to add lead. Please check the fields and try again.");
    } finally { setBusy(false); }
  };

  const field = (label, k, type = "text", ph = "") => (
    <div>
      <label className="block text-[11px] font-mono-tech uppercase tracking-wider text-slate-400 mb-1">{label}</label>
      <input type={type} value={f[k]} onChange={set(k)} placeholder={ph} data-testid={`new-lead-${k}`}
        className="w-full px-3 py-2.5 rounded-xl bg-white border border-[#E7E5DF] text-sm text-[#0B0F14] focus:outline-none focus:border-emerald-600/60" />
    </div>
  );

  return createPortal((
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="new-lead-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-[#FBFAF8] rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#0E3B3B] text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <h3 className="font-display font-extrabold text-lg flex items-center gap-2"><Plus size={18} /> Add New Lead</h3>
          <button onClick={onClose} data-testid="new-lead-close" className="text-white/70 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-6 grid sm:grid-cols-2 gap-4">
          {field("Contact Name *", "name", "text", "Jane Doe")}
          {field("Business Name", "business", "text", "Doe's Diner")}
          {field("Telephone *", "phone", "tel", "(305) 555-0100")}
          {field("Email", "email", "email", "jane@business.com")}
          {field("Service", "service", "text", "Business Waste / Roll-Off")}
          {field("Container Size", "container_size", "text", "e.g. 4 yard / 20 yard")}
          {field("Quantity", "quantity", "number", "1")}
          {field("Frequency", "frequency", "text", "e.g. 2x / week")}
          {field("Current Hauler", "hauler", "text", "e.g. Waste Management")}
          {field("Contract Expiration", "contract_expiration", "date")}
          {field("Current Competitor Price ($/mo)", "current_amount", "number", "e.g. 310")}
          {field("My Offered Price ($/mo)", "price", "number", "e.g. 240")}
          <div>
            <label className="block text-[11px] font-mono-tech uppercase tracking-wider text-slate-400 mb-1">Attachment</label>
            <label className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white border border-dashed border-[#c9c6bd] text-sm text-slate-500 cursor-pointer hover:border-emerald-600/60" data-testid="new-lead-attachment-label">
              <Paperclip size={14} /> {file ? file.name.slice(0, 22) : "Attach invoice / doc (image or PDF)"}
              <input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" className="hidden" data-testid="new-lead-attachment"
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-[11px] font-mono-tech uppercase tracking-wider text-slate-400 mb-1">Business / Service Address</label>
            <input type="text" value={f.service_address} onChange={set("service_address")} placeholder="123 Main St, Miami, FL 33176" data-testid="new-lead-service_address"
              className="w-full px-3 py-2.5 rounded-xl bg-white border border-[#E7E5DF] text-sm text-[#0B0F14] focus:outline-none focus:border-emerald-600/60" />
            <p className="text-[10px] text-slate-400 mt-1">Adds this lead to the map.</p>
          </div>
          <div className="sm:col-span-2">
            <label className="block text-[11px] font-mono-tech uppercase tracking-wider text-slate-400 mb-1">Notes</label>
            <textarea value={f.message} onChange={set("message")} rows={2} data-testid="new-lead-message"
              className="w-full px-3 py-2.5 rounded-xl bg-white border border-[#E7E5DF] text-sm text-[#0B0F14] focus:outline-none focus:border-emerald-600/60 resize-none" />
          </div>
        </div>
        <div className="sticky bottom-0 bg-[#FBFAF8] border-t border-[#E7E5DF] px-6 py-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2.5 rounded-full border border-[#E7E5DF] text-slate-600 text-sm font-semibold hover:border-slate-400">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="new-lead-submit"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Add Lead
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}

export default function PipelinePanel({ token, onExpire, onLeadUpdate }) {
  const [leads, setLeads] = useState(null);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [pct, setPct] = useState(12);
  const [pctInput, setPctInput] = useState("12");
  const [savingPct, setSavingPct] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [query, setQuery] = useState("");
  const [nudgeOnly, setNudgeOnly] = useState(false);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [q, s] = await Promise.all([
        axios.get(`${API}/admin/quotes`, { headers }),
        axios.get(`${API}/admin/settings`, { headers }),
      ]);
      setLeads(q.data);
      setPct(s.data.savings_percent);
      setPctInput(String(s.data.savings_percent));
    } catch (e) {
      if (e.response?.status === 401) onExpire?.();
      else toast.error("Failed to load pipeline.");
    } finally { setLoading(false); }
  }, [token, onExpire]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);

  const nudgeCount = useMemo(() => (leads || []).filter((lead) => isNudgeLead(lead, now)).length, [leads, now]);
  const filteredLeads = useMemo(() => {
    let list = leads || [];
    if (nudgeOnly) list = list.filter((lead) => isNudgeLead(lead, now));
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((l) => [l.name, l.business, l.phone, l.email, l.service_address]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q)));
    }
    return list;
  }, [leads, nudgeOnly, query, now]);

  const savePct = async () => {
    const v = parseFloat(pctInput);
    if (!v || v < 1 || v > 90) { toast.error("Enter a percent between 1 and 90."); return; }
    setSavingPct(true);
    try {
      await axios.patch(`${API}/admin/settings`, { savings_percent: v }, { headers: { Authorization: `Bearer ${token}` } });
      setPct(v);
      toast.success(`Savings rate set to ${v}%`);
    } catch { toast.error("Failed to save."); } finally { setSavingPct(false); }
  };

  const moveStage = async (id, stage) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, stage } : l)));
    onLeadUpdate?.(id, { stage });
    try {
      await axios.patch(`${API}/admin/quotes/${id}/stage`, { stage }, { headers: { Authorization: `Bearer ${token}` } });
    } catch { toast.error("Failed to move lead."); load(); }
  };

  const updateLead = (updated) => {
    onLeadUpdate?.(updated.id, updated);
    setLeads((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
    setActive(updated);
  };

  if (loading && !leads) return <div className="grid place-items-center py-20 text-slate-500"><Loader2 size={28} className="animate-spin" /></div>;

  const stageOf = (l) => (STAGES.some((s) => s.id === l.stage) ? l.stage : "new");

  return (
    <div data-testid="pipeline-panel">
      {/* Savings % setting */}
      <div className="flex flex-wrap items-center gap-3 mb-6 bg-white border border-[#E7E5DF] rounded-2xl p-4">
        <span className="flex items-center gap-2 font-semibold text-[#0B0F14] text-sm"><Percent size={16} className="text-emerald-600" /> Default savings rate</span>
        <div className="relative">
          <input type="number" value={pctInput} onChange={(e) => setPctInput(e.target.value)} data-testid="settings-savings-input"
            className="w-24 pl-3 pr-7 py-2 rounded-xl border border-[#E7E5DF] text-sm text-[#0B0F14] focus:outline-none focus:border-emerald-600/60" />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">%</span>
        </div>
        <button onClick={savePct} disabled={savingPct} data-testid="settings-savings-save"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#0E3B3B] text-white text-sm font-semibold hover:bg-[#0A2C2C] disabled:opacity-60">
          {savingPct ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
        </button>
        <span className="hidden sm:inline text-slate-400 text-xs">Used by the invoice calculator &amp; proposals (currently {pct}%).</span>
        <button onClick={() => setShowNew(true)} data-testid="add-lead-btn"
          className="w-full sm:w-auto sm:ml-auto inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700">
          <Plus size={15} /> Add Lead
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6" data-testid="pipeline-toolbar">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search customer, business, phone, email, address…"
            data-testid="pipeline-search"
            className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white border border-[#E7E5DF] text-sm text-[#0B0F14] placeholder:text-slate-400 focus:outline-none focus:border-emerald-600/60"
          />
        </div>
        <button
          type="button"
          onClick={() => setNudgeOnly((v) => !v)}
          aria-pressed={nudgeOnly}
          data-testid="pipeline-nudge-filter"
          className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-semibold transition-colors ${nudgeOnly ? "bg-amber-500 border-amber-500 text-white hover:bg-amber-600" : "bg-white border-[#E7E5DF] text-[#0B0F14] hover:border-amber-400"}`}
        >
          <BellRing size={15} /> Follow-up due
          <span className={`grid place-items-center min-w-5 h-5 px-1.5 rounded-full text-[11px] font-bold ${nudgeOnly ? "bg-white text-amber-600" : "bg-amber-500 text-white"}`} data-testid="pipeline-nudge-count">{nudgeCount}</span>
        </button>
      </div>

      {/* Kanban */}
      <div className="flex md:grid md:grid-cols-3 xl:grid-cols-5 gap-4 overflow-x-auto no-scrollbar snap-x -mx-4 px-4 pb-2 md:mx-0 md:px-0 md:pb-0 md:overflow-x-visible">
        {STAGES.map((stage) => {
          const items = filteredLeads.filter((l) => stageOf(l) === stage.id);
          return (
            <div key={stage.id} data-testid={`column-${stage.id}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { if (dragId) moveStage(dragId, stage.id); setDragId(null); }}
              className="bg-[#F0EFEA] rounded-2xl p-3 min-h-[200px] w-[78vw] shrink-0 snap-start md:w-auto md:shrink">
              <div className="flex items-center justify-between px-1 mb-3">
                <span className="flex items-center gap-2 font-display font-bold text-sm text-[#0B0F14]"><span className="w-2.5 h-2.5 rounded-full" style={{ background: stage.dot }} />{stage.label}</span>
                <span className="text-xs text-slate-500 bg-white rounded-full px-2 py-0.5">{items.length}</span>
              </div>
              <div className="space-y-2.5">
                {items.map((l) => (
                  <div key={l.id} draggable onDragStart={() => setDragId(l.id)} onClick={() => setActive(l)}
                    data-testid={`pipeline-card-${l.id}`}
                    className="group bg-white border border-[#E7E5DF] rounded-xl p-3 cursor-pointer hover:border-emerald-600/40 hover:shadow-sm transition-all">
                    <div className="flex items-start gap-1.5">
                      <GripVertical size={14} className="text-slate-300 mt-0.5 shrink-0 group-hover:text-slate-400" />
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-[#0B0F14] text-sm truncate" data-testid="card-name">{l.name}</p>
                        {l.business && <p className="text-slate-500 text-xs truncate">{l.business}</p>}
                        <p className="text-emerald-700 text-[11px] font-medium truncate mt-0.5">{l.service}</p>
                        {(l.container_size || l.frequency) && (
                          <p className="text-slate-500 text-[11px] truncate">{[l.container_size, l.frequency].filter(Boolean).join(" · ")}{l.quantity ? ` · x${l.quantity}` : ""}</p>
                        )}
                        {(l.current_amount || l.projected_amount) && (
                          <div className="mt-2 grid grid-cols-2 gap-1.5 text-[11px]" data-testid={`card-pricing-${l.id}`}>
                            <span className="rounded-lg bg-slate-100 px-2 py-1 text-slate-600">Competitor <b className="text-[#0B0F14]">{money(l.current_amount) || "—"}</b></span>
                            <span className="rounded-lg bg-emerald-50 px-2 py-1 text-emerald-700">My price <b>{money(l.projected_amount) || "—"}</b></span>
                          </div>
                        )}
                        <div className="flex flex-wrap gap-1 mt-1">
                          {isNudgeLead(l, now) && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white bg-amber-500 rounded px-1.5 py-0.5" data-testid={`pipeline-nudge-${l.id}`}><BellRing size={10} /> Follow up due</span>}
                          {money(l.savings_annual) && <span className="inline-block text-[11px] font-semibold text-emerald-700 bg-emerald-50 rounded px-1.5 py-0.5">Saves {money(l.savings_annual)}/yr</span>}
                          {l.invoice_path && <span className="inline-block text-[10px] font-semibold text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">Invoice</span>}
                        </div>
                        {l.proposal_sent_at && <p className="mt-1 text-[10px] text-amber-600 flex items-center gap-1"><FileText size={10} /> Proposal sent</p>}
                      </div>
                    </div>
                  </div>
                ))}
                {items.length === 0 && <p className="text-center text-slate-400 text-xs py-4">{query || nudgeOnly ? "No matching leads" : "Drop leads here"}</p>}
              </div>
            </div>
          );
        })}
      </div>

      {active && <LeadDrawer key={active.id} lead={active} token={token} onClose={() => setActive(null)} onUpdate={updateLead} />}
      {showNew && <NewLeadModal token={token} onClose={() => setShowNew(false)} onCreated={(l) => setLeads((prev) => [l, ...(prev || [])])} />}
    </div>
  );
}
